<template>
  <div class="rtw-history">
    <button type="button" class="history-toggle" :aria-expanded="open" :aria-controls="panelId" @click="toggle">
      History <AppointmentIcon name="chevron-down" :size="14" :class="{ collapsed: !open }" />
    </button>
    <div v-if="open" :id="panelId" :aria-busy="loading">
      <p v-if="error" class="history-error" role="alert">{{ error }} <button type="button" class="history-retry" @click="load">Retry</button></p>
      <p v-else-if="loading && !entries.length" class="history-empty" role="status">Loading history…</p>
      <ol v-else-if="entries.length" class="history-list">
        <li v-for="entry in entries" :key="entry.id">
          <details>
            <summary>
              <strong>{{ formatDate(entry.details.rightToWorkCheckDate) }}<template v-if="entry.details.rightToWorkCheckedBy"> · {{ entry.details.rightToWorkCheckedBy }}</template></strong>
              <AppointmentIcon name="chevron-down" :size="14" class="entry-chevron" />
              <span v-if="entry.current" class="history-current">Current</span>
            </summary>
            <p class="history-meta">{{ entry.recordedAt ? `Saved ${formatTime(entry.recordedAt)}${entry.actor?.name ? ` · ${entry.actor.name}` : ''}` : 'Previously saved verification' }}</p>
            <dl class="history-fields">
              <div v-for="field in fieldsFor(entry.details)" :key="field.key" :class="{ 'history-notes': field.type === 'textarea' }">
                <dt>{{ field.label }}</dt><dd>{{ fieldValue(field, entry.details) }}</dd>
              </div>
            </dl>
          </details>
        </li>
      </ol>
      <p v-else class="history-empty">No saved verifications yet.</p>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import { catalogTemplates } from '@/constants/catalogTemplates';
import { rightToWorkRequest } from '@/services/rightToWorkJournal';
import { onEvent } from '@/utils/eventBus';

const props = defineProps({ supplierId: { type: [Number, String], required: true } });
const open = ref(false);
const loading = ref(false);
const error = ref('');
const entries = ref([]);
const panelId = computed(() => `rtw-history-${props.supplierId}`);
let version = 0;
const fields = catalogTemplates.suppliers.rows.filter((row) => row.section === 'right-to-work-uk').flatMap((row) => row.fields).filter((field) => field.persist !== false);
const fieldsFor = (details) => fields.filter((field) => !field.visibleWhen || field.visibleWhen(details));
const formatDate = (value) => value ? value.slice(0, 10).split('-').reverse().join('.') : 'No check date';
const formatTime = (value) => new Date(value).toLocaleString('en-GB', { timeZone: 'Europe/London' });
const fieldValue = (field, details) => {
  const value = details[field.key];
  if (!value) return '—';
  if (field.type === 'date') return formatDate(value);
  return field.options?.find((option) => option.value === value)?.label || value;
};
async function load() {
  const request = ++version;
  loading.value = true;
  error.value = '';
  try {
    const result = await rightToWorkRequest(props.supplierId);
    if (request === version) entries.value = result.verifications || [];
  } catch (failure) {
    if (request === version) error.value = failure.message || 'Unable to load history.';
  } finally {
    if (request === version) loading.value = false;
  }
}
function toggle() {
  open.value = !open.value;
  if (open.value) void load();
}
const unsubscribe = onEvent('catalog:changed', ({ ids = [] } = {}) => {
  if (open.value && ids.some((id) => String(id) === String(props.supplierId))) void load();
});
watch(() => props.supplierId, () => { version++; open.value = false; entries.value = []; error.value = ''; loading.value = false; });
onBeforeUnmount(() => { version++; unsubscribe(); });
</script>

<style scoped>
.rtw-history { margin-top: 16px; color: var(--text-color, #474c5a); font: 14px/1.45 var(--font-family-base, sans-serif); }
.history-toggle, .history-retry { display: inline-flex; align-items: center; gap: 8px; min-height: 36px; padding: 6px 11px; border: 1px solid var(--border-color, #e0e3eb); border-radius: 6px; background: var(--noc-background-primary, #fff); color: inherit; font: 600 13px/20px var(--font-family-base, sans-serif); box-shadow: none; cursor: pointer; touch-action: manipulation; }
.history-toggle:hover, .history-retry:hover { background: var(--noc-background-secondary, #f5f7fb); }
.collapsed, .entry-chevron { transform: rotate(-90deg); }
details[open] > summary > .entry-chevron { transform: none; }
.history-list { margin: 12px 0 0; padding: 0; list-style: none; border: 1px solid var(--border-color, #e0e3eb); background: var(--noc-background-primary, #fff); }
.history-list li + li { border-top: 1px solid var(--border-color, #e0e3eb); }
.history-list summary { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; list-style: none; padding: 10px 12px; cursor: pointer; touch-action: manipulation; }
.history-list summary::-webkit-details-marker { display: none; }
.history-list strong { font-weight: 600; overflow-wrap: anywhere; }
.history-current { margin-left: auto; color: var(--noc-blue, #0065ff); font-size: 12px; }
.history-meta { margin: 0 12px 12px; color: var(--text-secondary, #7a8090); font-size: 12px; }
.history-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 24px; margin: 12px; }
.history-fields dt { color: var(--text-secondary, #7a8090); font-size: 12px; }
.history-fields dd { margin: 4px 0 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.history-notes { grid-column: 1 / -1; }
.history-empty { padding: 12px 0; color: var(--text-secondary, #7a8090); }
.history-error { color: #b43c3c; }
.rtw-history :is(button, summary):focus-visible { outline: 2px solid var(--noc-blue, #0065ff); outline-offset: 2px; }
@media (max-width: 600px) { .history-fields { grid-template-columns: 1fr; } }
</style>
